import { useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { HubConnectionBuilder, HubConnectionState, HttpTransportType, LogLevel } from '@microsoft/signalr'
import { API_ORIGIN } from '../utils/constants/url'
import localStorageIds from '../utils/constants/localStorageIds'
import QueryKeys from '../utils/constants/queryKeys'

export const LIVE_STREAMS = 'LiveStreams'
export const LIVE_STREAMS_CHANGED = 'LiveStreamsChanged'
export const GET_LIVE_STREAMS = 'GetLiveStreams'

// Pushes are best effort, so the list is also read on focus and, as a backstop, every minute.
const FALLBACK_REFETCH_MS = 60000

export const liveStreamsKey = (orgId) => [QueryKeys.GET_RUNNING_BROADCASTS_EVENTS, orgId]

/**
 * The org's live streams (events with a running stream, as the caller may see them) over the org's live
 * streams hub: the list on joining, and read again whenever the backend says it changed. Kept under the
 * running-broadcasts query key, so whatever invalidates that key reads the list again, through the hub.
 */
const useLiveStreams = (orgId, enabled) => {
  const queryClient = useQueryClient()
  const connectionRef = useRef(null)
  const key = liveStreamsKey(orgId)

  useEffect(() => {
    if (!enabled || !orgId) return undefined
    const token = localStorage.getItem(localStorageIds.TOKEN)
    if (!token) return undefined
    const queryKey = liveStreamsKey(orgId)

    // Straight over a websocket, as the stream hub does: the API's replicas share no negotiated
    // connection state, so the request that opens the socket must be the one that registers it.
    const connection = new HubConnectionBuilder()
      .withUrl(`${API_ORIGIN}/v1/orgs/${orgId}/streams/live?access_token=${encodeURIComponent(token)}`, {
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build()

    // Handlers return nothing: the server sends no invocation id, and SignalR logs an error for any
    // value handed back.
    connection.on(LIVE_STREAMS, (list) => {
      queryClient.setQueryData(queryKey, list)
    })
    connection.on(LIVE_STREAMS_CHANGED, () => {
      queryClient.invalidateQueries(queryKey)
    })
    connection.onreconnected(() => {
      queryClient.invalidateQueries(queryKey)
    })
    connectionRef.current = connection
    connection.start().catch(() => {
      // The reads on focus and the backstop keep the list honest without the hub.
    })

    return () => {
      connectionRef.current = null
      connection.stop()
    }
  }, [enabled, orgId, queryClient])

  return useQuery(
    key,
    () => {
      const connection = connectionRef.current
      // Until the hub is up, keep what is there: joining delivers the list anyway.
      if (connection?.state !== HubConnectionState.Connected) return queryClient.getQueryData(key) ?? []
      return connection.invoke(GET_LIVE_STREAMS)
    },
    {
      enabled: Boolean(enabled && orgId),
      refetchInterval: FALLBACK_REFETCH_MS,
      refetchOnWindowFocus: true,
      retry: false,
    },
  )
}

export default useLiveStreams
